import {Directive, EventEmitter, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {filter, take} from 'rxjs/operators';
import {CoreSidebarService} from '@core/components/core-sidebar/core-sidebar.service';
import {TranslocoService} from '@jsverse/transloco';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import {GlobalService} from 'app/global/global.service';
import {PermissionsEnum} from 'app/global/permissions/permissions.enum';
import {PermissionsService} from 'app/global/permissions/permissions.service';
import {TemplatePermissionsService} from 'app/global/permissions/template-permissions-service';
import {EditTemplateService} from '../../edit-template/edit-template.service';
import {SelectTemplateService} from '../../general-modals/select-template/select-template.service';
import {AccessLevelEnum} from '../../users/roles/roles.service';
import {PagingDto} from '../../users/userslisting/dto/paging.dto';
import {AddNewTemplateComponent} from '../add-new-template/add-new-template.component';
import {TemplateType} from '../template.dto';
import {IGetTemplateListItem, ITemplateFilters, TemplateService} from '../templates.service';
import {IPageableResponse} from 'app/global/pagging.dto';
import {SignableEformService} from '../../signable-eform/signable-eform.service';
import { GenerateTemplateModalService } from '../generate-template-modal.service';
import { GenerateFromDocumentOrPromptModalComponent } from '../../general-modals/generate-from-document-or-prompt/generate-from-document-or-prompt-modal.component';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import { ColumnConfig } from '../template-table/template-table.dto';

@Directive() // <-- Important: use @Directive for abstract base classes
export abstract class AbstractTemplateListingComponent implements  OnChanges {
  @Output() stoploader = new EventEmitter<void>();
  @ViewChild(DatatableComponent) table: DatatableComponent;
  public contentHeader: object;
  public page = 1;
  public templateListing: IGetTemplateListItem[] = [];
  public formLoader = false;
  public loading = false;
  public pagination: PagingDto = null;
  public renameTemplateForm: FormGroup;
  public submitted = false;
  public sendForSignatureLoader = false;
  public filters: ITemplateFilters;
  public search = '';
  public toggleTemplteFilter = false;
  public canBuildTemplates$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_build_templates);
  public templateType = TemplateType;
  public accessLeveLEnum = AccessLevelEnum;
  public displayTemplateListing: IGetTemplateListItem[] = [];
  public ColumnMode = ColumnMode;

  // New: central column configuration
  public columns: ColumnConfig[] = [
    { field: 'name', prop: 'template.name', nameKey: 'Template - Listview - Column name - Template', sortable: true, reorderable: true, resizable: false },
    { field: 'language', prop: 'template_details.language', nameKey: 'Language (general) - Language', sortable: true, reorderable: true, resizable: false },
    { field: 'owner', prop: 'template_owner.name', nameKey: 'Template - Listview - Column name - Owner', sortable: true, reorderable: true, resizable: false },
    { field: 'updated_at', prop: 'template.updated_at', nameKey: 'Template listing - Last edited (template listing section) - Last edited', sortable: true, reorderable: true, resizable: false },
    { field: 'status', prop: 'template.status', nameKey: 'Status column - label (general) - status', sortable: true, reorderable: false, resizable: false }
  ];
  constructor(
    protected _coreSidebarService: CoreSidebarService,
    protected templateService: TemplateService,
    protected globalService: GlobalService,
    protected activeRoute: ActivatedRoute,
    protected router: Router,
    protected modalService: NgbModal,
    protected formBuilder: FormBuilder,
    protected selectTemplateService: SelectTemplateService,
    protected editTemplateService: EditTemplateService,
    protected translocoService$: TranslocoService,
    protected _permissionService: PermissionsService,
    protected _templatePermissionService: TemplatePermissionsService,
    protected signableEformService: SignableEformService,
    protected generateTemplateModalService: GenerateTemplateModalService,
  ) {
    this.selectTemplateService.loading$.subscribe(
      (loading) => (this.sendForSignatureLoader = loading)
    );
  }

   // ---------------------------------------------------------------------
  // NEW: keep `displayTemplateListing` synchronized with `templateListing` input
  // and let any child automatically use `displayTemplateListing` for the datatable.
  // ---------------------------------------------------------------------
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateListing']) {
      this.displayTemplateListing = (this.templateListing || []).slice();
      if (this.table) {
        try { this.table.recalculate(); } catch (e) { console.warn('Error recalculating datatable:', e); }
      }
    }
  }

  
  /**
   * Safely extracts category IDs from a template, handling both new multi-category
   * templates (layout_settings.selected_category_ids) and legacy single-category templates (category_id).
   * 
   * @param template - The template item to extract category IDs from
   * @returns Array of category IDs, or empty array if none found
   */
  public getTemplateCategoryIds(template: IGetTemplateListItem): number[] {
    if (!template) return [];
    
    // Try new multi-category field first
    if (Array.isArray(template.layout_settings?.selected_category_ids) && 
        template.layout_settings.selected_category_ids.length > 0) {
      return template.layout_settings.selected_category_ids;
    }
    
    // Fallback to legacy single category_id from template_details
    if (typeof template.template_details?.category_id === 'number') {
      return [template.template_details.category_id];
    }
    
    // No category information available
    return [];
  }


    
    /**
     * Toggles the sidebar with the given reference name.
     *
     * @param nameRef - The reference name of the sidebar to toggle.
     * @returns void
     */
    toggleSidebar(nameRef: string): void {
      this._coreSidebarService.getSidebarRegistry(nameRef).toggleOpen();
    }
  
  
  
    
    
    /**
     * Toggles the visibility of the template filters.
     * This method inverts the current state of `toggleTemplteFilter`.
     */
    toggleTemplateFilters(): void {
      this.toggleTemplteFilter = !this.toggleTemplteFilter;
    }
  
  
    
  
    /**
     * Fetches the list of templates from the server.
     * 
     * @param {number} [page=1] - The page number to fetch.
     * @param {string} [q=''] - The query string to filter the templates.
     * @returns {void}
     * 
     * @remarks
     * This method sets the loading state to true and updates the current page number.
     * It then calls the `templateService.templateListing` method to fetch the templates.
     * On success, it updates the `templateListing` and `pagination` properties with the response data.
     * On error, it sets the loading state to false.
     * 
     * @emits stoploader - Emits an event to stop the loader once the data is fetched.
     */
    getTemplates(page: number = 1, q: string = ''): void {
      this.loading = true;
      this.page = page;
      const request = this.activeRoute.snapshot.data['key'] === TemplateType.EFORM ? this.signableEformService.getSignableEformList(page, q, this.filters) : this.templateService.templateListing(page, q, this.filters);
      request.subscribe({
        next: (response: IPageableResponse<IGetTemplateListItem>) => {
          this.templateListing = response.data || [];
          const pagingTmp = {...response};
          delete pagingTmp.data;
          this.pagination =  pagingTmp ;
          // keep display copy in sync
          this.displayTemplateListing = (this.templateListing || []).slice();
          this.loading = false;
          this.stoploader.emit();
        }, 
        error: () => this.loading = false
      });
    }
  
  
  
    
    /**
     * Publishes a template by updating its status and making a service call.
     *
     * @param status - An object containing a boolean `checked` property indicating the new status of the template.
     * @param index - The index of the template in the `templateListing` array.
     * @param event - The event object associated with the publish action.
     *
     * This method prevents the default action and stops propagation of the event.
     * It updates the status of the template in the `templateListing` array and calls the `publishTemplate` method
     * of the `templateService` to update the status on the server.
     * On success, it displays a success message using `globalService.toastrSuccess`.
     * On error, it displays an error message using `globalService.toastrError`.
     * 
     * @returns {void}
     */
    publishTemplate(status: {checked: boolean}, index: number, event: Event): void {
      event.preventDefault();
      event.stopPropagation();
      this.templateListing[index]['template']['status'] = status.checked ? 1 : 0;
      this.templateService.publishTemplate({id: this.templateListing[index]['template'].id, status: (status.checked ? 1 : 0)})
        .subscribe({
          next: (response) => this.globalService.toastrSuccess(response.message),
          error: (error) => this.globalService.toastrError(error)
      });
  
    }
  
  
  
  
   
    /**
     * Navigates to the edit area of a template if the user has the required permissions.
     *
     * @param template - The template item containing details required for navigation and permission checks.
     * 
     * The function first constructs an object containing the user ID and workspace ID from the template's branding information.
     * It then checks if the user has the necessary permissions to use and edit the template's document type.
     * If the user has the required permissions, it navigates to the edit-template route with the template ID and passes the template data in the navigation state.
     */
    goToEditArea(template: IGetTemplateListItem): void {
      const IResult = {user_id: template?.template?.user_id, workspace_id: template?.template?.workspace_id};
      const categoryIds = this.getTemplateCategoryIds(template);
      
      this._templatePermissionService.verifyPermission(template?.template?.id, template?.template_details?.document_type_id, categoryIds, IResult).pipe(
        filter(x => [AccessLevelEnum.full_access, AccessLevelEnum.use_and_edit].includes(x)),
        take(1)
      ).subscribe(() => {
        const navigationExtras: NavigationExtras = {
          state: {
            data: template?.template
          }
        };
        this.router.navigate(['/edit-template', template?.template?.id], navigationExtras);
      });
    }
  
  
  
    
  
    /**
     * Deletes a template after user confirmation.
     * 
     * This method displays a confirmation dialog to the user. If the user confirms,
     * it sends a request to delete the specified template. Upon successful deletion,
     * it shows a success message and refreshes the template list. If an error occurs,
     * it shows an error message.
     * 
     * @param data - The template data to be deleted, including the template ID and name.
     */
    deleteTemplate(data: IGetTemplateListItem): void {
      Swal.fire({
        title: this.translocoService$.translate('User confirmation (general) - Are you sure'),
        text: this.translocoService$.translate('User confirmation (general) - Do you want to delete', {name: data.template.name}),
        showCancelButton: true,
        confirmButtonText: this.translocoService$.translate('Modal button (general) - Yes'),
        confirmButtonColor: '#ef0016',
        cancelButtonText: this.translocoService$.translate('Modal button (general) - No'),
      }).then(async (result) => {
        if (result.value) {
          this.templateService.deleteTemplate(data.template.id).subscribe({
            next: () => {
              this.globalService.toastrSuccess(this.translocoService$.translate("Toaster success message for delete template (Template listing) - Template has been deleted"));
              this.getTemplates();     
            },
            error: (error) => this.globalService.toastrError(error)
          });
        }
      });
    }
  
  
  
  
  
    
    /**
     * Opens a modal to duplicate a template.
     *
     * @param template - The template item to be duplicated.
     * @param templateModal - The template reference for the modal.
     * @returns A promise that resolves when the modal is opened.
     */
    async duplicateTemplateModal(template: IGetTemplateListItem, templateModal: TemplateRef<unknown>): Promise<void> {
      await this.initContractForm(template);
      this.modalService.open(templateModal);
    }
  
  
    /** Get controls of duplicate template modal */
    get form() {
      return this.renameTemplateForm.controls;
    }
  
  
  
  
    
    /**
     * Initializes the contract form with the provided template data.
     *
     * @param {IGetTemplateListItem} template - The template data used to initialize the form.
     * @returns {void}
     */
    initContractForm(template: IGetTemplateListItem): void {
      this.renameTemplateForm = this.formBuilder.group({
        id: [template.template.id],
        name: [template.template.name ?? '', [Validators.required]],
        fields_data: [template.template_fields_datas.data]
      });
    }
  
  
    
  
    /**
     * Duplicates a template by sending a request to the server.
     * 
     * This method sets the submitted and formLoader flags to true to indicate that the form is being submitted.
     * If the form is invalid, it returns early. Otherwise, it sends a request to duplicate the template.
     * On success, it resets the form and dismisses the modal. It then calls the `getTemplates` method to refresh the template list.
     * On error, it shows an error message.
     */
    duplicateTemplate(): void {
      this.submitted = true;
      if (this.renameTemplateForm.invalid) {
        return;
      }
      this.formLoader = true;
      this.templateService.duplicateTemplate(this.renameTemplateForm.value).subscribe({
        next: async () => {
          this.submitted = false;
          this.formLoader = false;
          this.modalService.dismissAll();
          this.getTemplates();
        },
        error: (error) => {
          this.submitted = false;
          this.formLoader = false;
          this.globalService.toastrError(error);
        },
      });
    }
  
  
  
    /**
     * Opens a modal to send a template for signature.
     *
     * @param template - The template item to be sent for signature.
     * @param template_id - The ID of the template to be sent for signature.
     * @returns {void}
     */
    sendForSignature(template: IGetTemplateListItem, template_id: number): void {
      const details = template.template_details;
      details.category_ids = details.categories?.map(c => c.id) || [];
      this.selectTemplateService.goToSendDocument({ ...template, template_details: details }, template_id);
    }
  
  
  
  
    /**
     * Opens a modal to add a new template.
     *
     * @param templateType - The type of the template to be added.
     * @returns {void}
     */
    addNewTemplate(templateType: string): void {
      const instance$ = this.modalService.open(AddNewTemplateComponent);
      instance$.componentInstance.canBuildTemplates$ = this.canBuildTemplates$;
      instance$.componentInstance.template_type = templateType;
    }
    
    
    generateTemplate(): void {
        const modal = this.modalService.open(GenerateFromDocumentOrPromptModalComponent);
        modal.componentInstance.generationService = this.generateTemplateModalService;
        modal.componentInstance.showOptions = true;
        modal.componentInstance.title = this.translocoService$.translate("Modal title (AI template generation modal) - New Template");
        modal.componentInstance.submitButtonTitle = this.translocoService$.translate("Submission button title (AI document generation modal) - Generate Template");
    }

    onSearch(query: string): void {
      this.search = query;
      this.getTemplates(1, query);
    }

    onPageChange(page: number){
      this.getTemplates(page, this.search);
    }

     /**
       * Helper used by templates to map a datatable row back to the original index
       * in the `templateListing` array (some existing base-class methods expect
       * an index into the original input array).
     */
    public getOriginalIndex(row: IGetTemplateListItem): number {
      if (!this.templateListing || !row) return -1;
      return this.templateListing.findIndex(t => t?.template?.id === row?.template?.id);
    }
}